import React, { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, ShieldAlert, RefreshCcw, Eye, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/* Admin → Fabric Colors: Quality check panel for regenerated blackout renders.
   Uses AI vision to flag renders that still show light/transparency. */
export const BlackoutQcPanel = ({ token, refreshKey }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/admin/fabrics/blackout-renders/qc/status`, { headers });
      setStatus(data);
    } catch { /* optional panel */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load, refreshKey]);

  /* Poll while a job is running */
  useEffect(() => {
    if (!status?.latest_job || status.latest_job.status !== 'running') return;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [status?.latest_job?.status, load]);

  const runQc = async (limit) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/admin/fabrics/blackout-renders/qc/run`, limit ? { limit } : {}, { headers });
      toast.info(`QC running: checking ${data.total} render${data.total === 1 ? '' : 's'}`);
      setTimeout(load, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not start QC');
    } finally {
      setBusy(false);
    }
  };

  const recheckColor = async (colorId) => {
    try {
      await axios.post(`${API_URL}/admin/fabrics/blackout-renders/qc/recheck`, { color_ids: [colorId] }, { headers });
      toast.info('Re-checking render...');
      setTimeout(load, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Recheck failed');
    }
  };

  const regenerateColor = async (colorId) => {
    try {
      await axios.post(`${API_URL}/admin/fabrics/blackout-renders/regenerate`, { color_ids: [colorId] }, { headers });
      toast.info('Regenerating render...');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Regeneration failed');
    }
  };

  if (!status || status.total_regenerated === 0) return null;

  const isRunning = status.latest_job?.status === 'running';
  const hasFlagged = status.failed > 0;

  return (
    <div className="rounded-xl border border-neutral-200 bg-white px-4 py-3 space-y-3" data-testid="blackout-qc-panel">
      {/* Summary row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          {hasFlagged ? (
            <ShieldAlert className="w-5 h-5 mt-0.5 text-red-500 shrink-0" />
          ) : status.checked > 0 ? (
            <ShieldCheck className="w-5 h-5 mt-0.5 text-green-600 shrink-0" />
          ) : (
            <ShieldCheck className="w-5 h-5 mt-0.5 text-neutral-400 shrink-0" />
          )}
          <div className="text-sm">
            <p className="font-medium text-neutral-900">Opaque Render QC</p>
            <p className="text-neutral-500" data-testid="blackout-qc-summary">
              {status.checked === 0
                ? `${status.total_regenerated} renders ready to check`
                : (
                  <>
                    <span className="text-green-600 font-medium">{status.passed} passed</span>
                    {status.failed > 0 && <> · <span className="text-red-500 font-medium">{status.failed} flagged</span></>}
                    {status.unchecked > 0 && <> · {status.unchecked} unchecked</>}
                  </>
                )}
              {isRunning && ` · Checking ${status.latest_job.completed}/${status.latest_job.total}...`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isRunning && <Loader2 className="w-4 h-4 animate-spin text-amber-500" />}
          {!isRunning && status.unchecked > 0 && (
            <Button size="sm" onClick={() => runQc()} disabled={busy} className="gap-1.5 bg-amber-500 text-neutral-900 hover:bg-amber-400" data-testid="blackout-qc-run-btn">
              <Eye className="w-3.5 h-3.5" /> Check {status.unchecked} render{status.unchecked === 1 ? '' : 's'}
            </Button>
          )}
          {hasFlagged && (
            <Button size="sm" variant="outline" onClick={() => setExpanded(!expanded)} className="gap-1.5" data-testid="blackout-qc-expand-btn">
              <AlertTriangle className="w-3.5 h-3.5 text-red-500" /> {expanded ? 'Hide' : 'View'} flagged ({status.failed})
            </Button>
          )}
        </div>
      </div>

      {/* Flagged renders detail */}
      {expanded && hasFlagged && (
        <div className="space-y-2 border-t border-neutral-100 pt-3" data-testid="blackout-qc-flagged-list">
          {status.flagged.map((item) => {
            const imgUrl = item.image_url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${item.image_url}` : item.image_url;
            return (
              <div key={item.color_id} className="flex items-start gap-3 p-2 rounded-lg bg-red-50 border border-red-100" data-testid={`qc-flagged-${item.color_id}`}>
                {imgUrl && (
                  <div className="w-16 h-16 rounded-md overflow-hidden flex-shrink-0 border border-red-200">
                    <img src={imgUrl} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-neutral-900">{item.name} <span className="text-neutral-400 text-xs">({item.collection})</span></p>
                  <p className="text-xs text-red-600 mt-0.5">{item.summary}</p>
                  {item.issues?.length > 0 && (
                    <ul className="text-xs text-neutral-600 mt-1 space-y-0.5">
                      {item.issues.map((issue, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <span className="text-red-400 mt-0.5">•</span> {issue}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="flex gap-2 mt-1.5">
                    <button onClick={() => regenerateColor(item.color_id)} className="text-xs text-amber-700 hover:text-amber-900 font-medium" data-testid={`qc-regen-${item.color_id}`}>
                      Re-generate
                    </button>
                    <button onClick={() => recheckColor(item.color_id)} className="text-xs text-blue-600 hover:text-blue-800 font-medium" data-testid={`qc-recheck-${item.color_id}`}>
                      Re-check
                    </button>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                    <AlertTriangle className="w-3 h-3" /> {Math.round((item.confidence || 0) * 100)}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* All passed celebration */}
      {status.checked > 0 && status.failed === 0 && status.unchecked === 0 && (
        <div className="flex items-center gap-2 text-green-700 text-sm bg-green-50 rounded-lg px-3 py-2" data-testid="blackout-qc-all-passed">
          <CheckCircle2 className="w-4 h-4" />
          All {status.passed} blackout renders passed the opaque quality check
        </div>
      )}
    </div>
  );
};

export default BlackoutQcPanel;
